import { cloneDeep, isEqual } from 'lodash'

import { Services, isEmpty, joinURLPath } from '@sigma-frontend-libs/commons'

import { keyValue, properties, request } from '@/utils'

import render from './render'

const defaultPayload = {
  key: undefined,
  value: undefined
}
const defaultKeyValueMapPayload = { '': '' }

export const propertiesSelect = (options = {}) => {
  return {
    name: `${options.serviceName}Properties`,

    mixins: [ keyValue, properties(options) ],

    props: {
      processor: String,
      ServerId: String
    },

    data() {
      return {
        editMode: false,
        showDialog: false,

        index: undefined,

        payload: cloneDeep(defaultPayload),

        processorFromServer: undefined
      }
    },

    computed: {
      $_processor() {
        return this.processor || this.processorFromServer
      },

      hint() {
        let result = ''
        if (this.payload.key) {
          const property = this._getProperty()
          if (Array.isArray(property.processor)) {
            result += this.getTranslate(`${Services.servers}.labels.processor`) + ': '
            result += property.processor.join(' | ')
            result += '<br>'
          }
          result += property.description
        }

        return result
      },

      type() {
        if (this.payload.key) {
          const { type } = this._getProperty()

          return type
        }
      },

      payloadIsEqual() {
        const proxyValue = this.proxy.find(({ key }) => {
          return key === this.payload.key
        })
        if (proxyValue) {
          const payload = this.valueProcessor(this.payload.key, this.payload.value)

          return isEqual(proxyValue.value, payload)
        }

        return false
      },

      disabled() {
        if (!this.$_processor) {
          return true
        }

        switch (this.payload.key) {
          case 'telegramGateway_apiTokens':
          case 'd360ApiKeys':
          case 'teleleo_apiKeys': {
            return Object.keys(this.payload.value || {}).length === 0
          }
          case 'megafonTarget_campaignTimeWindowHrs': {
            if (!isEmpty(this.payload.value) && !this.payloadIsEqual) {
              return this.payload.value.every(item => {
                return isEmpty(item)
              })
            }

            return true
          }
          case 'megafonTarget_senderNameToIds': {
            if (!isEmpty(this.payload.value) && !this.payloadIsEqual) {
              return this.payload.value.some(item => {
                return Object.values(item).some(value => {
                  return isEmpty(value)
                })
              })
            }

            return true
          }
          default: return isEmpty(this.payload.key) || isEmpty(this.payload.value)
        }
      }
    },

    watch: {
      'payload.key'(newValue, oldValue) {
        if (newValue) {
          const { type, property } = this._getProperty()
          switch (type) {
            case 'string':
            case 'number': {
              if (oldValue !== undefined && typeof this.payload.value !== type) {
                this.payload.value = undefined
              }
              break
            }
            case 'boolean': {
              if (typeof this.payload.value !== 'boolean') {
                this.payload.value = false
              }
              break
            }
          }
          switch (property) {
            case 'telegramGateway_apiTokens':
            case 'd360ApiKeys':
            case 'teleleo_apiKeys': {
              if (typeof this.payload.value !== 'object' || !this.payload.value || Object.keys(this.payload.value).length === 0) {
                this.payload.value = cloneDeep(defaultKeyValueMapPayload)
              }
              break
            }
            case 'forceMessageStatus': {
              if (
                typeof this.payload.value !== 'object' ||
                  !Array.isArray(this.payload.value.conditions) ||
                  this.payload.value.status !== undefined &&
                  typeof this.payload.value.status !== 'string'
              ) {
                this.payload.value = {
                  conditions: [],
                  status: undefined
                }
              }
              break
            }
            case 'megafonTarget_campaignTimeWindowHrs': {
              if (!Array.isArray(this.payload.value)) {
                this.payload.value = []
              }
              break
            }
            case 'megafonTarget_senderNameToIds': {
              if (oldValue === undefined) {
                if (!isEmpty(this.payload.value)) {
                  this.payload.value = Object.keys(this.payload.value).map(key => {
                    return {
                      sendername: key,
                      ...this.payload.value[key]
                    }
                  }).sort((a, b) => {
                    const va = String(a.sendername || '').toLocaleLowerCase()
                    const vb = String(b.sendername || '').toLocaleLowerCase()

                    return va.localeCompare(vb)
                  })
                }
              } else {
                this.payload.value = []
              }
              break
            }
          }
        }
      },
      showDialog(value) {
        if (value === false) {
          this.cancel()
        }
      },

      ServerId() {
        this.getProcessor()
      }
    },

    mounted() {
      this.getProcessor()
    },

    methods: {
      cancel() {
        this.payload = cloneDeep(defaultPayload)
        if (this.editMode) {
          this.index = undefined
          this.editMode = false
        }
        this.showDialog = false
      },
      add() {
        this.proxy.push(this.payload)
        this.cancel()
      },
      edit(index) {
        this.index = index
        this.payload = cloneDeep(this.proxy[index])
        this.editMode = true
        this.showDialog = true
      },
      update() {
        this.proxy.splice(this.index, 1, this.payload)
        this.cancel()
      },

      async getProcessor() {
        if (this.ServerId) {
          const data = await request.get(joinURLPath(Services.servers, this.ServerId)) || {}
          this.processorFromServer = data.processor
        } else {
          this.processorFromServer = undefined
          this.proxy.splice(0, this.proxy.length)
        }
      },

      valueProcessor(property, value) {
        switch (property) {
          case 'megafonTarget_campaignTimeWindowHrs': return value.map(Number)
          case 'megafonTarget_senderNameToIds': {
            if (Array.isArray(value) && value.length) {
              const result = {}

              for (const { sendername, advertiserId, senderNumberId } of value) {
                if (!isEmpty(sendername)) {
                  result[sendername] = {
                    advertiserId,
                    senderNumberId
                  }
                }
              }

              return result
            } else {
              return value
            }
          }
          default: return value
        }
      },

      propertiesFilter({ whiteList, blackList }) {
        if (!this.$_processor) {
          return true
        }
        if (Array.isArray(blackList) && blackList.length) {
          if (~blackList.indexOf(this.$_processor)) {
            return false
          }
        }
        if (!Array.isArray(whiteList) || !whiteList.length) {
          return true
        }
        if (~whiteList.indexOf(this.$_processor)) {
          return true
        }

        return false
      },
      searchValidator(searchValue, { description, group }) {
        searchValue = searchValue.toLocaleLowerCase()
        description = description.toLocaleLowerCase()
        const tag = group.tag.toLocaleLowerCase()
        const text = group.text.toLocaleLowerCase()

        return ~description.indexOf(searchValue) || ~tag.indexOf(searchValue) || ~text.indexOf(searchValue)
      },

      _inputFilter(data) {
        return Object.keys(data).reduce((result, key) => {
          result.splice(result.length, 0, {
            key,
            value: data[key]
          })

          return result
        }, [])
      },
      _outputFilter(data) {
        return data.reduce((result, item) => {
          return Object.assign(result, { [item.key]: this.valueProcessor(item.key, item.value) })
        }, {})
      },

      _getProperty() {
        const key = this.payload.key
        if (key) {
          return this.properties.find(item => {
            return item.property === key
          })
        }

        return {}
      }
    },

    render(h) {
      return render.call(this, h, options)
    }
  }
}
